import React, { useState, useEffect, useMemo } from "react";
import axios from "axios"; 
import { useNavigate } from "react-router-dom";
import { jobsEndpoint } from '../constants';
// Import your avatar images
import Avatar2 from "../assets/Avatar2.png";
import Avatar3 from "../assets/Avatar3.png";
import Avatar4 from "../assets/Avatar4.png";
import Avatar5 from "../assets/Avatar5.png";
import Avatar6 from "../assets/Avatar6.png";
import Avatar from "../assets/Avatar.png";

const SharedApplicants = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Avatar fallbacks
  const avatars = [Avatar, Avatar2, Avatar3, Avatar4, Avatar5, Avatar6];
  
  // Status options based on your Django model
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'REVIEWED', label: 'Reviewed' },
    { value: 'SHORTLISTED', label: 'Shortlisted' },
    { value: 'INTERVIEWED', label: 'Interviewed' },
    { value: 'OFFERED', label: 'Offered' },
    { value: 'HIRED', label: 'Hired' },
    { value: 'REJECTED', label: 'Rejected' }
  ];

useEffect(() => {
    const fetchApplicants = async () => {
      try {
        let url =   `${jobsEndpoint}applicant/shared/`;
        if (statusFilter) {
          url += `?status=${statusFilter}`;
        }

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (Array.isArray(data.results)) {
          setApplicants(data.results);
        } else {
          console.error('Unexpected data format:', data);
          setError('Unexpected data format received');
          setApplicants([]);
        }
      } catch (error) {
        console.error('Failed to fetch applicants:', error);
        setError(error.message);
        setApplicants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, [token, statusFilter]);


  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filtered and sorted applicants
  const filteredApplicants = useMemo(() => {
    let result = [...applicants];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (applicant) =>
          (applicant.cv_file_names && applicant.cv_file_names.some(name => 
            name.toLowerCase().includes(searchLower)
          )) ||
          (applicant.ai_rating && applicant.ai_rating.toLowerCase().includes(searchLower)) ||
          (applicant.source && applicant.source.toLowerCase().includes(searchLower)) ||
          (applicant.status && applicant.status.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle special cases
        if (sortConfig.key === 'date_created') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [applicants, searchTerm, sortConfig]);

  const getStatusColor = (status) => {
    const statusColors = {
      'PENDING': 'border-yellow-400 bg-yellow-100 text-yellow-600',
      'REVIEWED': 'border-blue-400 bg-blue-100 text-blue-600',
      'SHORTLISTED': 'border-green-400 bg-green-100 text-green-600',
      'INTERVIEWED': 'border-indigo-400 bg-indigo-100 text-indigo-600',
      'OFFERED': 'border-green-500 bg-green-200 text-green-700',
      'HIRED': 'border-green-600 bg-green-200 text-green-800',
      'REJECTED': 'border-red-400 bg-red-100 text-red-600'
    };
    return statusColors[status] || 'border-gray-400 bg-gray-100 text-gray-600';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 font-bold';
    if (score >= 60) return 'text-yellow-600 font-bold';
    return 'text-red-600 font-bold';
  };

  // Function to get avatar for applicant
  const getApplicantAvatar = (applicantId) => {
    return avatars[applicantId % avatars.length];
  };

  // Function to get display name for applicant
  const getDisplayName = (applicant) => {
    if (applicant.cv_file_names && applicant.cv_file_names.length > 0) {
      const firstName = applicant.cv_file_names[0].split('_')[0] || `Applicant ${applicant.id}`;
      return firstName.length > 20 ? firstName.substring(0, 20) : firstName;
    }
    return `Applicant #${applicant.id}`;
  };

  // Function to handle image load errors and provide fallback
  const handleImageError = (e, applicantId) => {
    e.target.style.display = 'none';
    const initials = `A${applicantId}`;
    const fallbackDiv = document.createElement('div');
    fallbackDiv.className = 'w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium text-sm';
    fallbackDiv.textContent = initials;
    e.target.parentNode.insertBefore(fallbackDiv, e.target);
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7] mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading candidates...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md mx-auto">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-[#19bfb7] text-white px-4 py-2 rounded hover:bg-[#18ada6] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Candidates</h1>
            <p className="mt-1 text-sm text-gray-600">
              Here is a complete list of all CV applicants.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-8 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl">
          {/* Search Input */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search candidates..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent text-black bg-white"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent bg-white text-black"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value} className="text-black">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => navigate("/CreateCandidate")}
          className="px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] transition-colors"
        >
          Add Candidate
        </button>
      </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="pt-3 px-5">
          <h3 className="text-sm font-semibold text-gray-900">
            Total Shared Candidates
          </h3>
        </div>
        <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200 rounded-t-md">
          <h3 className="text-2xl text-gray-900 font-semibold">{filteredApplicants.length}</h3>
          <div className="flex items-center gap-1 text-sm text-emerald-600">
            <span>↑ {statusFilter ? 'Filtered' : 'Total'}</span>
            <span className="text-gray-600 ml-1">candidates</span>
          </div>
        </div>

        <div>
          {/* Table Header */}
          <div className="px-5 py-3 grid md:grid-cols-8 sm:grid-cols-5 grid-cols-4 border-b border-gray-200">
            <div className="flex gap-3 items-center md:col-span-3 col-span-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-sm cursor-pointer"
              />
              <h5 className="text-gray-500 font-semibold cursor-pointer" onClick={() => handleSort('id')}>
                Candidate
                {sortConfig.key === 'id' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
            </div>

            <div className="grid md:grid-cols-5 sm:grid-cols-4 grid-cols-2 md:col-span-5 sm:col-span-3 col-span-2">
              <h5 className="text-gray-500 font-semibold sm:block hidden md:text-sm text-xs cursor-pointer" onClick={() => handleSort('source')}>
                Source
                {sortConfig.key === 'source' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
              <h5 className="text-gray-500 font-semibold md:text-sm text-xs cursor-pointer" onClick={() => handleSort('cv_count')}>
                CV Files
                {sortConfig.key === 'cv_count' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
              <h5 className="text-gray-500 font-semibold md:block hidden md:text-sm text-xs cursor-pointer" onClick={() => handleSort('ai_score')}>
                AI Score
                {sortConfig.key === 'ai_score' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
              <h5 className="text-gray-500 font-semibold sm:block hidden md:text-sm text-xs cursor-pointer" onClick={() => handleSort('status')}>
                Status
                {sortConfig.key === 'status' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
              <h5 className="text-gray-500 font-semibold md:block hidden md:text-sm text-xs cursor-pointer" onClick={() => handleSort('date_created')}>
                Date
                {sortConfig.key === 'date_created' && (
                  <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                )}
              </h5>
            </div>
          </div>

          {/* Table Body */}
          {filteredApplicants.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 text-6xl mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {searchTerm || statusFilter ? 'No candidates found' : 'No candidates available'}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm || statusFilter 
                  ? 'Try adjusting your search terms or filters.'
                  : 'Candidates will appear here once CVs are uploaded and processed.'
                }
              </p>
            </div>
          ) : (
            filteredApplicants.map((applicant) => (
              <div
                key={applicant.id}
                className="grid md:grid-cols-8 sm:grid-cols-5 grid-cols-4 items-center p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/candidate_profile`, { state: { candidate: applicant } })}
              >
                {/* Candidate Info */}
                <div className="flex items-center gap-3 md:col-span-3 col-span-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 sm:block hidden"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="md:block hidden">
                    <img
                      src={getApplicantAvatar(applicant.id)}
                      alt={`Applicant ${applicant.id} avatar`}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => handleImageError(e, applicant.id)}
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 sm:text-lg text-sm">
                      {getDisplayName(applicant)}
                    </p>
                    <p className="sm:text-sm text-xs text-gray-500">
                      ID: #{applicant.id}
                    </p>
                  </div>
                </div>

                {/* Source */}
                <div className="sm:block hidden">
                  <p className="font-medium md:text-sm text-xs text-gray-500">
                    {applicant.source || 'N/A'}
                  </p>
                </div>

                {/* CV Files */}
                <div>
                  <p className="font-medium md:text-sm text-xs text-gray-500">
                    {applicant.cv_count || 0} files
                  </p>
                </div>

                {/* AI Score */}
                <div className="md:block hidden">
                  <p className={`font-medium md:text-sm text-xs ${applicant.ai_score ? getScoreColor(applicant.ai_score) : 'text-gray-400'}`}>
                    {applicant.ai_score || 'N/A'}
                  </p>
                </div>

                {/* Status */}
                <div className="sm:flex hidden justify-start items-center">
                  <span
                    className={`text-center text-[12px] border py-1 px-2 rounded-md ${getStatusColor(applicant.status)}`}
                  >
                    {applicant.status}
                  </span>
                </div>

                {/* Date */}
                <div className="md:block hidden">
                  <p className="font-medium md:text-sm text-xs text-gray-500">
                    {new Date(applicant.date_published).toLocaleDateString()}
                  </p>
                </div>

                {/* Mobile Actions */}
                <div className="sm:hidden flex justify-center items-center">
                  <span className="text-gray-600 cursor-pointer text-xl font-bold">
                    &#8942;
                  </span>
                </div>
              </div>
            ))
          )}

          {/* Table Footer */}
          <div className="px-5 py-4 flex items-center justify-between bg-gray-50">
            <div>
              <p className="text-md font-semibold text-gray-900">
                Showing {filteredApplicants.length} of {applicants.length} candidates
              </p>
            </div>
            <div className="flex gap-5 items-center">
              <button className="text-sm bg-white border border-gray-300 text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                Previous
              </button>
              <button className="text-sm bg-white text-gray-900 py-2 px-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedApplicants;